import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Package, Save, X } from "lucide-react";
import { sumSizeQuantities } from "../../utils/fashionSizes";

const qtyInputClass =
  "w-20 h-9 px-2.5 text-sm text-right tabular-nums border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]";

/**
 * Right-side drawer for editing per-size / variant stock quantities.
 */
const VariantsStockDrawer = ({
  isOpen,
  item,
  initialSizes = [],
  isSaving = false,
  onClose,
  onApply,
  onSave,
}) => {
  const [sizes, setSizes] = useState([]);

  useEffect(() => {
    if (!isOpen || !item) return;
    setSizes(
      (initialSizes || []).map((row) => ({
        size: row.size,
        quantity: String(row.quantity ?? "0"),
      }))
    );
    // Only re-seed when opening for a product — not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.slug]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !isSaving) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, isSaving, onClose]);

  const total = useMemo(() => sumSizeQuantities(sizes), [sizes]);

  const updateQty = (index, value) => {
    const cleaned = value.replace(/[^\d]/g, "");
    setSizes((prev) =>
      prev.map((row, idx) =>
        idx === index ? { ...row, quantity: cleaned } : row
      )
    );
  };

  const handleApply = () => {
    onApply?.(sizes);
    onClose?.();
  };

  const handleSave = () => {
    onSave?.(sizes);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && item ? (
        <>
          <motion.div
            key="variants-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/40"
            onClick={() => !isSaving && onClose?.()}
          />

          <motion.aside
            key="variants-drawer-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed inset-y-0 right-0 z-[70] flex w-full max-w-md flex-col bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="variants-drawer-title"
          >
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-100 bg-gray-50">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      className="h-full w-full object-contain object-center"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-300">
                      <Package className="h-4 w-4" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h2
                    id="variants-drawer-title"
                    className="truncate text-base font-semibold text-gray-900"
                  >
                    Update variants
                  </h2>
                  <p className="mt-0.5 truncate text-sm text-gray-500">
                    {item.title}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isSaving && onClose?.()}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Total available
                </span>
                <span className="text-lg font-semibold tabular-nums text-gray-900">
                  {total}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="mb-3 text-sm text-gray-500">
                Set the quantity for each size variant. Total stock updates
                automatically.
              </p>

              {sizes.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-400">
                  No variants on this product.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {sizes.map((row, idx) => (
                    <li
                      key={row.size}
                      className="flex items-center justify-between gap-4 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {row.size}
                        </p>
                        <p className="text-xs text-gray-400">Size variant</p>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.quantity}
                        onChange={(e) => updateQty(idx, e.target.value)}
                        aria-label={`Quantity for size ${row.size}`}
                        className={qtyInputClass}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={() => !isSaving && onClose?.()}
                disabled={isSaving}
                className="h-9 rounded-md px-3 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={isSaving || sizes.length === 0}
                className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || sizes.length === 0}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#FF6C6C] px-3 text-sm font-semibold text-white hover:bg-[#e85c5c] disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save stock
              </button>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default VariantsStockDrawer;
